import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import { ArrowLeft, Sparkles, Video, Loader2, Clock, CheckCircle, XCircle, Play } from 'lucide-react';
import { toast } from 'sonner';

const VideoLab = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(4);
  const [size, setSize] = useState('1280x720');
  const [submitting, setSubmitting] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef();

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => load(true), 8000);
    return () => clearInterval(pollRef.current);
  }, []);

  const load = async (silent = false) => {
    try {
      const r = await apiService.listMyVideos();
      setVideos(r.data.videos);
    } catch (e) { if (!silent) toast.error('Failed'); }
    finally { if (!silent) setLoading(false); }
  };

  const submit = async () => {
    if (prompt.trim().length < 10) { toast.error('Describe the video in at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await apiService.generateVideo({ prompt, duration, size });
      toast.success('Video generation started! It takes 1-3 minutes.');
      setPrompt('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const examples = [
    'A clear animation showing the Pythagorean theorem with a right triangle and labeled squares on each side',
    'A bouncing ball demonstrating potential and kinetic energy on a hill',
    'A simple animation showing how plants do photosynthesis with the sun, water, and CO2',
  ];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="video-lab">
      <div className="bg-gradient-to-r from-violet-900 via-purple-900 to-fuchsia-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-400/20 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white font-semibold mb-3"><ArrowLeft className="w-5 h-5" />Dashboard</button>
          <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3"><Video className="w-8 h-8" />AI Video Lab</h1>
          <p className="text-white/70 text-sm sm:text-base">Describe a concept and Sora 2 will create a short explainer video for you.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-5">
            <h2 className="font-black text-[#1E293B] mb-3">Describe your video</h2>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={5} maxLength={500} data-testid="video-prompt"
              placeholder="e.g. An animation explaining the water cycle with rain, evaporation and clouds..."
              className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-sm focus:border-violet-500 outline-none resize-none mb-3" />
            <p className="text-[10px] text-slate-400 text-right mb-3">{prompt.length}/500</p>

            <label className="block text-xs font-bold text-slate-600 mb-1">Duration (seconds)</label>
            <select value={duration} onChange={e => setDuration(parseInt(e.target.value))} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm mb-3" data-testid="video-duration">
              {[4, 6, 8, 12].map(d => <option key={d} value={d}>{d}s</option>)}
            </select>

            <label className="block text-xs font-bold text-slate-600 mb-1">Aspect</label>
            <select value={size} onChange={e => setSize(e.target.value)} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm mb-4">
              <option value="1280x720">Landscape 1280x720</option>
              <option value="1024x1024">Square 1024x1024</option>
              <option value="1024x1792">Portrait 1024x1792</option>
            </select>

            <motion.button onClick={submit} disabled={submitting} whileTap={{ scale: 0.98 }} data-testid="video-submit"
              className="w-full py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl font-black flex items-center justify-center gap-2 disabled:opacity-50 shadow">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" />Submitting...</> : <><Sparkles className="w-5 h-5" />Generate Video</>}
            </motion.button>
            
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Example ideas</p>
              {examples.map((e, i) => (
                <button key={i} onClick={() => setPrompt(e)} className="block w-full text-left text-xs bg-slate-50 hover:bg-violet-50 rounded-lg p-2 mb-1.5 text-slate-700">
                  {e}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <h2 className="font-black text-[#1E293B] mb-3">My Videos</h2>
          {loading ? <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto" /> :
            videos.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                <Video className="w-16 h-16 text-slate-300 mx-auto mb-2" />
                <p className="font-bold text-slate-500">No videos yet</p>
                <p className="text-xs text-slate-400">Create your first concept video on the left</p>
              </div>
            ) : (
              <div className="space-y-3">
                {videos.map(v => (
                  <div key={v.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid={`video-${v.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="font-bold text-sm text-[#1E293B] line-clamp-2 flex-1">{v.prompt}</p>
                      <StatusBadge status={v.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                      <span>{v.duration}s</span>
                      <span>{v.size}</span>
                      <span>{new Date(v.created_at).toLocaleString()}</span>
                    </div>
                    {v.status === 'completed' && v.video_path && (
                      <video src={`/api/static/videos/${v.id}.mp4`} controls className="w-full rounded-xl bg-black" />
                    )}
                    {v.status === 'processing' && (
                      <div className="bg-violet-50 rounded-xl p-3 text-sm text-violet-700 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />Sora 2 is creating your video...
                      </div>
                    )}
                    {v.status === 'failed' && (
                      <div className="bg-red-50 rounded-xl p-3 text-xs text-red-700">{v.error || 'Generation failed'}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { color: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" />, label: 'Queued' },
    processing: { color: 'bg-violet-100 text-violet-700', icon: <Loader2 className="w-3 h-3 animate-spin" />, label: 'Generating' },
    completed: { color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Ready' },
    failed: { color: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3" />, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  return <span className={`${s.color} px-2 py-0.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 flex-shrink-0`}>{s.icon}{s.label}</span>;
};

export default VideoLab;
